import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, getImageUrl } from '../utils/api';
import './AdminPage.css';

interface User {
  id: string;
  name: string;
  email: string;
  tier?: string;
  monthlyCredits?: number;
  paidTokens?: number;
  totalTokens?: number;
  hasApiAccess: boolean;
  isAdmin: boolean;
  isLocked: boolean;
  appliedCoupon?: string;
  storageUsed: number;
  createdAt: string;
  lastActive?: string;
}

interface Coupon {
  id: string;
  code: string;
  discountPercent: number;
  usageLimit: number;
  usedCount: number;
  expiresAt?: string;
  isActive: boolean;
  createdBy?: {
    _id: string;
    name: string;
    email: string;
  };
  ledger: Array<{
    userId: string;
    userName: string;
    userEmail: string;
    action: 'created' | 'used';
    timestamp: string;
  }>;
  createdAt: string;
  canBeUsed: boolean;
}

interface Transaction {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  type: 'ai_generation' | 'coupon_usage' | 'token_purchase' | 'token_adjustment';
  aiGeneration?: {
    generationType: 'character' | 'background' | 'asset' | 'scene';
    prompt: string;
    storyType?: string;
    drawingType?: string;
    storyTone?: string;
    canvasSize?: string;
    responseImageUrl?: string;
    openaiRequestId?: string;
    openaiModel?: string;
    success: boolean;
    errorMessage?: string;
    tokensUsed: number;
  };
  couponUsage?: {
    couponCode: string;
    couponId: string;
    tokensReceived: number;
  };
  tokenPurchase?: {
    amount: number;
    tokensReceived: number;
    paymentMethod?: string;
    paymentProvider?: string;
    paymentId?: string;
    billingEmail?: string;
    billingName?: string;
    billingAddress?: string;
    invoiceId?: string;
    status: 'pending' | 'completed' | 'failed' | 'refunded';
  };
  tokenAdjustment?: {
    amount: number;
    reason: string;
    adminId: string;
    adminName: string;
  };
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
}

interface TransactionStats {
  [key: string]: {
    count: number;
    totalTokens: number;
  };
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'coupons'>('users');
  
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
  
  const getStoragePercentage = (bytes: number) => {
    const limit = 500 * 1024 * 1024; // 500MB
    return Math.round((bytes / limit) * 100);
  };

  const formatLastActive = (lastActive?: string) => {
    if (!lastActive) return '-';
    const date = new Date(lastActive);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString();
  };
  const [users, setUsers] = useState<User[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [selectedUserForTransactions, setSelectedUserForTransactions] = useState<{ id: string; name: string; email: string; currentTokens: number } | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionStats, setTransactionStats] = useState<TransactionStats>({});
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionTotalPages, setTransactionTotalPages] = useState(1);
  const [transactionFilter, setTransactionFilter] = useState<string>('');
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [expandedTransaction, setExpandedTransaction] = useState<string | null>(null);
  
  // Token grant state
  const [showGrantTokenModal, setShowGrantTokenModal] = useState(false);
  const [grantTokenUserId, setGrantTokenUserId] = useState<string>('');
  const [grantTokenUserName, setGrantTokenUserName] = useState<string>('');
  const [grantTokenAmount, setGrantTokenAmount] = useState<number>(100);
  const [grantTokenReason, setGrantTokenReason] = useState<string>('');
  const [grantTokenLoading, setGrantTokenLoading] = useState(false);
  
  // File management state
  const [selectedUserForFiles, setSelectedUserForFiles] = useState<{ id: string; name: string; email: string; storageUsed: number } | null>(null);
  const [userFiles, setUserFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [recalculatingStorage, setRecalculatingStorage] = useState(false);
  
  // Coupon form state
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discountPercent: 10,
    usageLimit: 1,
    expiresAt: ''
  });

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadUsers();
    loadCoupons();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      const data = await adminAPI.getUsers();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCoupons = async () => {
    try {
      const data = await adminAPI.getCoupons();
      if (data.success) {
        setCoupons(data.coupons);
      }
    } catch (error) {
      console.error('Failed to load coupons:', error);
    }
  };

  const loadUserTransactions = async (userId: string, userName: string, userEmail: string, currentTokens: number) => {
    setSelectedUserForTransactions({ id: userId, name: userName, email: userEmail, currentTokens });
    setLoadingTransactions(true);
    setTransactionPage(1);
    
    try {
      const data = await adminAPI.getUserTransactions(userId, 1, 50, transactionFilter || undefined);
      if (data.success) {
        setTransactions(data.transactions);
        setTransactionStats(data.stats);
        setTransactionTotalPages(data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
      alert('Failed to load user transactions');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadMoreTransactions = async (page: number) => {
    if (!selectedUserForTransactions) return;
    
    setLoadingTransactions(true);
    try {
      const data = await adminAPI.getUserTransactions(
        selectedUserForTransactions.id,
        page,
        50,
        transactionFilter || undefined
      );
      if (data.success) {
        setTransactions(data.transactions);
        setTransactionPage(page);
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleCreateCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCoupon.code.trim()) {
      alert('Please enter a coupon code');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/coupons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newCoupon)
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Coupon created successfully!');
        setNewCoupon({ code: '', discountPercent: 10, usageLimit: 1, expiresAt: '' });
        loadCoupons();
      } else {
        console.error('Server error:', data);
        alert(data.error || data.errors?.[0]?.msg || 'Failed to create coupon');
      }
    } catch (error) {
      console.error('Failed to create coupon:', error);
      alert('Failed to create coupon');
    }
  };

  const handleToggleCoupon = async (couponId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/coupons/${couponId}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        loadCoupons();
      }
    } catch (error) {
      console.error('Failed to toggle coupon:', error);
    }
  };

  const handleViewFiles = async (userId: string, userName: string, userEmail: string, storageUsed: number) => {
    setSelectedUserForFiles({ id: userId, name: userName, email: userEmail, storageUsed });
    setLoadingFiles(true);
    
    try {
      const response = await adminAPI.getUserFiles(userId);
      if (response.success && response.data && response.data.files) {
        setUserFiles(response.data.files);
      } else {
        setUserFiles([]);
      }
    } catch (error) {
      console.error('Failed to load user files:', error);
      alert('Failed to load user files');
      setUserFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleRecalculateStorage = async () => {
    if (!selectedUserForFiles) return;
    
    if (!window.confirm(`Recalculate storage for ${selectedUserForFiles.name}? This will scan all their storybooks and update their storage usage.`)) {
      return;
    }
    
    setRecalculatingStorage(true);
    try {
      const response = await adminAPI.recalculateUserStorage(selectedUserForFiles.id);
      if (response.success && response.data && response.data.storageUsed !== undefined) {
        alert(`Storage recalculated successfully!\nOld: ${formatBytes(selectedUserForFiles.storageUsed)}\nNew: ${formatBytes(response.data.storageUsed)}`);
        
        // Refresh users list and file view
        loadUsers();
        setSelectedUserForFiles({ 
          ...selectedUserForFiles, 
          storageUsed: response.data.storageUsed 
        });
        
        // Reload files
        const filesResponse = await adminAPI.getUserFiles(selectedUserForFiles.id);
        if (filesResponse.success && filesResponse.data && filesResponse.data.files) {
          setUserFiles(filesResponse.data.files);
        }
      } else {
        alert('Failed to recalculate storage: Invalid response from server');
      }
    } catch (error) {
      console.error('Failed to recalculate storage:', error);
      alert('Failed to recalculate storage');
    } finally {
      setRecalculatingStorage(false);
    }
  };

  const handleDeleteCoupon = async (couponId: string) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        loadCoupons();
      }
    } catch (error) {
      console.error('Failed to delete coupon:', error);
    }
  };

  const handleToggleApiAccess = async (userId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userId}/api-access`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        loadUsers();
      }
    } catch (error) {
      console.error('Failed to toggle API access:', error);
    }
  };

  const handleToggleAdmin = async (userId: string) => {
    if (!window.confirm('Are you sure you want to change admin status for this user?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userId}/admin`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        loadUsers();
      } else {
        alert(data.error || 'Failed to update admin status');
      }
    } catch (error) {
      console.error('Failed to toggle admin status:', error);
    }
  };

  const handleToggleLock = async (userId: string, isCurrentlyLocked: boolean) => {
    const action = isCurrentlyLocked ? 'unlock' : 'lock';
    const confirmMessage = isCurrentlyLocked 
      ? 'Are you sure you want to unlock this user account?' 
      : 'Are you sure you want to lock this user account? They will not be able to log in.';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userId}/${action}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        loadUsers();
      } else {
        alert(data.error || `Failed to ${action} user account`);
      }
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      alert(`Failed to ${action} user account`);
    }
  };

  const handleGrantTokens = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (grantTokenAmount < 1 || grantTokenAmount > 10000) {
      alert('Token amount must be between 1 and 10,000');
      return;
    }

    if (!grantTokenReason.trim()) {
      alert('Please provide a reason for granting tokens');
      return;
    }

    setGrantTokenLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${grantTokenUserId}/grant-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount: grantTokenAmount,
          reason: grantTokenReason
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Successfully granted ${data.granted} tokens to ${grantTokenUserName}.\n\nRemaining weekly quota: ${data.remainingQuota} / ${data.weeklyLimit} tokens`);
        setShowGrantTokenModal(false);
        setGrantTokenAmount(100);
        setGrantTokenReason('');
        loadUsers();
      } else {
        alert(data.error || 'Failed to grant tokens');
      }
    } catch (error) {
      console.error('Failed to grant tokens:', error);
      alert('Failed to grant tokens');
    } finally {
      setGrantTokenLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>🛠️ Admin Panel</h1>
      </header>

      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users ({users.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'coupons' ? 'active' : ''}`}
          onClick={() => setActiveTab('coupons')}
        >
          🎫 Coupons ({coupons.length})
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="admin-content">
          <div className="active-users-stats">
            <div className="stat-card active-now">
              <span className="stat-icon">🟢</span>
              <span className="stat-value">{users.filter(u => u.lastActive && (Date.now() - new Date(u.lastActive).getTime()) < 60 * 60 * 1000).length}</span>
              <span className="stat-label">Active (last hour)</span>
            </div>
            <div className="stat-card active-today">
              <span className="stat-icon">📊</span>
              <span className="stat-value">{users.filter(u => u.lastActive && (Date.now() - new Date(u.lastActive).getTime()) < 24 * 60 * 60 * 1000).length}</span>
              <span className="stat-label">Active (last 24h)</span>
            </div>
          </div>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Tokens</th>
                  <th>Tier</th>
                  <th>Storage</th>
                  <th>API Access</th>
                  <th>Admin</th>
                  <th>Lock</th>
                  <th>Coupon</th>
                  <th>Last Active</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...users].sort((a, b) => {
                  const aTime = a.lastActive ? new Date(a.lastActive).getTime() : 0;
                  const bTime = b.lastActive ? new Date(b.lastActive).getTime() : 0;
                  return bTime - aTime;
                }).map(u => (
                  <tr key={u.id}>
                    <td>
                      <span 
                        className="clickable-user-name" 
                        onClick={() => loadUserTransactions(u.id, u.name, u.email, u.totalTokens || 0)}
                        title="Click to view transaction history"
                      >
                        {u.name}
                      </span>
                    </td>
                    <td>{u.email}</td>
                    <td className="tokens-cell">
                      <div style={{ fontSize: '12px' }}>
                        <div><strong>{(u.totalTokens || 0).toLocaleString()}</strong> total</div>
                        <div style={{ color: '#6366f1' }}>({(u.monthlyCredits || 0).toLocaleString()} monthly + {(u.paidTokens || 0).toLocaleString()} paid)</div>
                      </div>
                    </td>
                    <td className="tokens-cell" style={{ color: '#6366f1', textTransform: 'capitalize' }}>
                      {u.tier || 'free'}
                    </td>
                    <td className="storage-cell">
                      <div className="storage-info">
                        <span className="storage-value">{formatBytes(u.storageUsed)}</span>
                        <span className="storage-percent" style={{
                          color: getStoragePercentage(u.storageUsed) > 90 ? '#dc3545' : 
                                 getStoragePercentage(u.storageUsed) > 70 ? '#ffc107' : '#6c757d'
                        }}>
                          ({getStoragePercentage(u.storageUsed)}%)
                        </span>
                      </div>
                    </td>
                    <td>
                      <button
                        className={`btn btn-small ${u.hasApiAccess ? 'btn-success' : 'btn-secondary'}`}
                        onClick={() => handleToggleApiAccess(u.id)}
                      >
                        {u.hasApiAccess ? '✓ Enabled' : '✗ Disabled'}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`btn btn-small ${u.isAdmin ? 'btn-warning' : 'btn-secondary'}`}
                        onClick={() => handleToggleAdmin(u.id)}
                        disabled={u.id === user?.id}
                      >
                        {u.isAdmin ? '👑 Admin' : 'User'}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`btn btn-small ${u.isLocked ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => handleToggleLock(u.id, u.isLocked)}
                        disabled={u.id === user?.id || u.isAdmin}
                      >
                        {u.isLocked ? '🔒 Locked' : '🔓 Active'}
                      </button>
                    </td>
                    <td>{u.appliedCoupon || '-'}</td>
                    <td>{formatLastActive(u.lastActive)}</td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => {
                            setGrantTokenUserId(u.id);
                            setGrantTokenUserName(u.name);
                            setShowGrantTokenModal(true);
                          }}
                          title="Grant tokens to this user"
                        >
                          💰 Grant Tokens
                        </button>
                        <button
                          className="btn btn-small btn-info"
                          onClick={() => handleViewFiles(u.id, u.name, u.email, u.storageUsed)}
                          title="View user's files"
                        >
                          📁 View Files
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'coupons' && (
        <div className="admin-content">
          <div className="coupon-form">
            <h2>Create New Coupon</h2>
            <form onSubmit={handleCreateCoupon}>
              <div className="form-row">
                <div className="form-group">
                  <label>Coupon Code:</label>
                  <input
                    type="text"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., WELCOME10"
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Discount % (= Bonus Tokens):</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCoupon.discountPercent}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discountPercent: parseInt(e.target.value) })}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Usage Limit (max 100):</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCoupon.usageLimit}
                    onChange={(e) => setNewCoupon({ ...newCoupon, usageLimit: parseInt(e.target.value) })}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Expires At (optional):</label>
                  <input
                    type="date"
                    value={newCoupon.expiresAt}
                    onChange={(e) => setNewCoupon({ ...newCoupon, expiresAt: e.target.value })}
                    className="form-control"
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary">
                Create Coupon
              </button>
            </form>
          </div>

          <div className="coupons-table-container">
            <h2>Existing Coupons</h2>
            <table className="coupons-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount %</th>
                  <th>Usage</th>
                  <th>Created By</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <tr key={c.id} className={!c.isActive ? 'inactive' : ''}>
                    <td className="code-cell">{c.code}</td>
                    <td>{c.discountPercent}%</td>
                    <td>
                      <span 
                        className="usage-link"
                        onClick={() => setSelectedCoupon(c)}
                        style={{ cursor: 'pointer', color: '#3B82F6', textDecoration: 'underline' }}
                      >
                        {c.usedCount} / {c.usageLimit}
                      </span>
                    </td>
                    <td className="creator-cell">
                      {c.createdBy ? (
                        <div>
                          <div style={{ fontWeight: 500 }}>{c.createdBy.name}</div>
                          <div style={{ fontSize: '11px', color: '#6B7280' }}>{c.createdBy.email}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>System</span>
                      )}
                    </td>
                    <td>{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : 'Never'}</td>
                    <td>
                      <span className={`status-badge ${c.canBeUsed ? 'valid' : 'invalid'}`}>
                        {c.canBeUsed ? '✓ Valid' : '✗ Invalid'}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`btn btn-small ${c.isActive ? 'btn-warning' : 'btn-secondary'}`}
                        onClick={() => handleToggleCoupon(c.id)}
                      >
                        {c.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDeleteCoupon(c.id)}
                        style={{ marginLeft: '8px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Ledger Modal */}
          {selectedCoupon && (
            <div className="modal-overlay" onClick={() => setSelectedCoupon(null)}>
              <div className="modal ledger-modal" onClick={(e) => e.stopPropagation()}>
                <h2>Coupon Ledger: {selectedCoupon.code}</h2>
                <div className="ledger-info">
                  <p><strong>Discount:</strong> {selectedCoupon.discountPercent}%</p>
                  <p><strong>Usage:</strong> {selectedCoupon.usedCount} / {selectedCoupon.usageLimit}</p>
                </div>
                <div className="ledger-entries">
                  <h3>History</h3>
                  {selectedCoupon.ledger && selectedCoupon.ledger.length > 0 ? (
                    <table className="ledger-table">
                      <thead>
                        <tr>
                          <th>Action</th>
                          <th>User</th>
                          <th>Email</th>
                          <th>Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCoupon.ledger
                          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                          .map((entry, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className={`action-badge ${entry.action}`}>
                                {entry.action === 'created' ? '➕ Created' : '✓ Used'}
                              </span>
                            </td>
                            <td>{entry.userName}</td>
                            <td>{entry.userEmail}</td>
                            <td>{new Date(entry.timestamp).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="empty-message">No ledger entries available</p>
                  )}
                </div>
                <button className="btn btn-secondary" onClick={() => setSelectedCoupon(null)}>
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Transaction Modal */}
          {selectedUserForTransactions && (
            <div className="modal-overlay" onClick={() => setSelectedUserForTransactions(null)}>
              <div className="modal transaction-modal" onClick={(e) => e.stopPropagation()}>
                <h2>Transaction History: {selectedUserForTransactions.name}</h2>
                <div className="transaction-user-info">
                  <p><strong>Email:</strong> {selectedUserForTransactions.email}</p>
                  <p><strong>Current Tokens:</strong> {selectedUserForTransactions.currentTokens.toLocaleString()}</p>
                </div>

                {/* Transaction Statistics */}
                <div className="transaction-stats">
                  <h3>Statistics</h3>
                  <div className="stats-grid">
                    {transactionStats.ai_generation && (
                      <div className="stat-card">
                        <span className="stat-label">AI Generations</span>
                        <span className="stat-value">{transactionStats.ai_generation.count}</span>
                        <span className="stat-tokens">-{transactionStats.ai_generation.totalTokens.toLocaleString()} tokens</span>
                      </div>
                    )}
                    {transactionStats.coupon_usage && (
                      <div className="stat-card">
                        <span className="stat-label">Coupons Used</span>
                        <span className="stat-value">{transactionStats.coupon_usage.count}</span>
                        <span className="stat-tokens">+{transactionStats.coupon_usage.totalTokens.toLocaleString()} tokens</span>
                      </div>
                    )}
                    {transactionStats.token_adjustment && (
                      <div className="stat-card">
                        <span className="stat-label">Admin Adjustments</span>
                        <span className="stat-value">{transactionStats.token_adjustment.count}</span>
                        <span className="stat-tokens">
                          {transactionStats.token_adjustment.totalTokens >= 0 ? '+' : ''}
                          {transactionStats.token_adjustment.totalTokens.toLocaleString()} tokens
                        </span>
                      </div>
                    )}
                    {transactionStats.token_purchase && (
                      <div className="stat-card">
                        <span className="stat-label">Purchases</span>
                        <span className="stat-value">{transactionStats.token_purchase.count}</span>
                        <span className="stat-tokens">+{transactionStats.token_purchase.totalTokens.toLocaleString()} tokens</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Filter */}
                <div className="transaction-filter">
                  <label>Filter by type:</label>
                  <select 
                    value={transactionFilter} 
                    onChange={(e) => {
                      setTransactionFilter(e.target.value);
                      if (selectedUserForTransactions) {
                        loadUserTransactions(
                          selectedUserForTransactions.id,
                          selectedUserForTransactions.name,
                          selectedUserForTransactions.email,
                          selectedUserForTransactions.currentTokens
                        );
                      }
                    }}
                  >
                    <option value="">All Types</option>
                    <option value="ai_generation">AI Generation</option>
                    <option value="coupon_usage">Coupon Usage</option>
                    <option value="token_adjustment">Token Adjustment</option>
                    <option value="token_purchase">Token Purchase</option>
                  </select>
                </div>

                {/* Transaction List */}
                <div className="transaction-list">
                  {loadingTransactions ? (
                    <p>Loading transactions...</p>
                  ) : transactions.length === 0 ? (
                    <p className="empty-message">No transactions found</p>
                  ) : (
                    transactions.map(tx => (
                      <div key={tx._id} className="transaction-item">
                        <div className="transaction-header" onClick={() => setExpandedTransaction(expandedTransaction === tx._id ? null : tx._id)}>
                          <span className={`transaction-type-badge ${tx.type}`}>
                            {tx.type === 'ai_generation' && '🎨 AI Generation'}
                            {tx.type === 'coupon_usage' && '🎟️ Coupon'}
                            {tx.type === 'token_adjustment' && '⚙️ Adjustment'}
                            {tx.type === 'token_purchase' && '💳 Purchase'}
                          </span>
                          <span className="transaction-date">
                            {new Date(tx.timestamp).toLocaleString()}
                          </span>
                          <span className={`transaction-tokens ${
                            tx.type === 'ai_generation' ? 'negative' :
                            tx.type === 'coupon_usage' || tx.type === 'token_purchase' ? 'positive' :
                            (tx.tokenAdjustment?.amount || 0) >= 0 ? 'positive' : 'negative'
                          }`}>
                            {tx.type === 'ai_generation' && `-${tx.aiGeneration?.tokensUsed}`}
                            {tx.type === 'coupon_usage' && `+${tx.couponUsage?.tokensReceived}`}
                            {tx.type === 'token_purchase' && `+${tx.tokenPurchase?.tokensReceived}`}
                            {tx.type === 'token_adjustment' && `${(tx.tokenAdjustment?.amount || 0) >= 0 ? '+' : ''}${tx.tokenAdjustment?.amount}`}
                          </span>
                          <span className="expand-icon">{expandedTransaction === tx._id ? '▼' : '▶'}</span>
                        </div>

                        {expandedTransaction === tx._id && (
                          <div className="transaction-details">
                            {/* AI Generation Details */}
                            {tx.aiGeneration && (
                              <div className="detail-section">
                                <p><strong>Type:</strong> {tx.aiGeneration.generationType}</p>
                                <p><strong>Model:</strong> {tx.aiGeneration.openaiModel}</p>
                                <p><strong>Status:</strong> <span className={tx.aiGeneration.success ? 'status-success' : 'status-failed'}>
                                  {tx.aiGeneration.success ? '✓ Success' : '✗ Failed'}
                                </span></p>
                                {tx.aiGeneration.errorMessage && (
                                  <p><strong>Error:</strong> {tx.aiGeneration.errorMessage}</p>
                                )}
                                <div className="prompt-section">
                                  <strong>Prompt:</strong>
                                  <div className="prompt-text">{tx.aiGeneration.prompt}</div>
                                </div>
                                {tx.aiGeneration.storyType && <p><strong>Story Type:</strong> {tx.aiGeneration.storyType}</p>}
                                {tx.aiGeneration.drawingType && <p><strong>Drawing Type:</strong> {tx.aiGeneration.drawingType}</p>}
                                {tx.aiGeneration.storyTone && <p><strong>Tone:</strong> {tx.aiGeneration.storyTone}</p>}
                                {tx.aiGeneration.canvasSize && <p><strong>Canvas:</strong> {tx.aiGeneration.canvasSize}</p>}
                                {tx.aiGeneration.responseImageUrl && tx.aiGeneration.success && (
                                  <div className="generated-image">
                                    <strong>Generated Image:</strong>
                                    <img src={getImageUrl(tx.aiGeneration.responseImageUrl)} alt="Generated" />
                                  </div>
                                )}
                                {tx.aiGeneration.openaiRequestId && (
                                  <p className="request-id"><strong>Request ID:</strong> <code>{tx.aiGeneration.openaiRequestId}</code></p>
                                )}
                              </div>
                            )}

                            {/* Coupon Usage Details */}
                            {tx.couponUsage && (
                              <div className="detail-section">
                                <p><strong>Coupon Code:</strong> {tx.couponUsage.couponCode}</p>
                                <p><strong>Tokens Received:</strong> +{tx.couponUsage.tokensReceived}</p>
                              </div>
                            )}

                            {/* Token Purchase Details */}
                            {tx.tokenPurchase && (
                              <div className="detail-section">
                                <p><strong>Amount:</strong> ${tx.tokenPurchase.amount}</p>
                                <p><strong>Tokens:</strong> +{tx.tokenPurchase.tokensReceived}</p>
                                <p><strong>Status:</strong> <span className={`status-${tx.tokenPurchase.status}`}>{tx.tokenPurchase.status}</span></p>
                                {tx.tokenPurchase.paymentProvider && <p><strong>Provider:</strong> {tx.tokenPurchase.paymentProvider}</p>}
                                {tx.tokenPurchase.paymentId && <p><strong>Payment ID:</strong> <code>{tx.tokenPurchase.paymentId}</code></p>}
                                {tx.tokenPurchase.invoiceId && <p><strong>Invoice:</strong> <code>{tx.tokenPurchase.invoiceId}</code></p>}
                                {tx.tokenPurchase.billingName && <p><strong>Billing Name:</strong> {tx.tokenPurchase.billingName}</p>}
                                {tx.tokenPurchase.billingEmail && <p><strong>Billing Email:</strong> {tx.tokenPurchase.billingEmail}</p>}
                              </div>
                            )}

                            {/* Token Adjustment Details */}
                            {tx.tokenAdjustment && (
                              <div className="detail-section">
                                <p><strong>Amount:</strong> {tx.tokenAdjustment.amount >= 0 ? '+' : ''}{tx.tokenAdjustment.amount}</p>
                                <p><strong>Reason:</strong> {tx.tokenAdjustment.reason}</p>
                                <p><strong>Admin:</strong> {tx.tokenAdjustment.adminName}</p>
                              </div>
                            )}

                            {/* Common metadata */}
                            <div className="meta-info">
                              {tx.ipAddress && <p><strong>IP:</strong> {tx.ipAddress}</p>}
                              {tx.userAgent && <p><strong>User Agent:</strong> {tx.userAgent}</p>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {transactionTotalPages > 1 && (
                  <div className="pagination">
                    <button 
                      disabled={transactionPage === 1} 
                      onClick={() => loadMoreTransactions(transactionPage - 1)}
                    >
                      Previous
                    </button>
                    <span>Page {transactionPage} of {transactionTotalPages}</span>
                    <button 
                      disabled={transactionPage === transactionTotalPages} 
                      onClick={() => loadMoreTransactions(transactionPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}

                <button className="btn btn-secondary" onClick={() => setSelectedUserForTransactions(null)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* File Management Modal */}
      {selectedUserForFiles && (
        <div className="modal-overlay" onClick={() => setSelectedUserForFiles(null)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <h2>📁 Files: {selectedUserForFiles.name}</h2>
            <div className="user-info-header">
              <p><strong>Email:</strong> {selectedUserForFiles.email}</p>
              <p><strong>Storage Used:</strong> {formatBytes(selectedUserForFiles.storageUsed)} / 500 MB ({getStoragePercentage(selectedUserForFiles.storageUsed)}%)</p>
            </div>

            <div className="file-management-actions">
              <button
                className="btn btn-primary"
                onClick={handleRecalculateStorage}
                disabled={recalculatingStorage}
              >
                {recalculatingStorage ? '🔄 Recalculating...' : '🔄 Recalculate Storage'}
              </button>
            </div>

            {loadingFiles ? (
              <div className="loading-container">
                <p>Loading files...</p>
              </div>
            ) : (
              <>
                {userFiles.length === 0 ? (
                  <p className="no-files-message">No files found for this user.</p>
                ) : (
                  <div className="files-table-container">
                    <table className="files-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Filename</th>
                          <th>Size</th>
                          <th>Storybook</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userFiles.map((file, index) => (
                          <tr key={index}>
                            <td>
                              <span className={`file-type-badge ${file.type}`}>
                                {file.type === 'character' && '👤'}
                                {file.type === 'background' && '🏞️'}
                                {file.type === 'scene' && '🎬'}
                                {file.type === 'asset' && '🎨'}
                                {file.type === 'page-image' && '📄'}
                                {' '}{file.type}
                              </span>
                            </td>
                            <td className="filename-cell">
                              <code>{file.filename}</code>
                            </td>
                            <td>{formatBytes(file.size)}</td>
                            <td>
                              {file.storybook ? (
                                <span>{file.storybook.title}</span>
                              ) : (
                                <span className="orphaned">Orphaned</span>
                              )}
                            </td>
                            <td>
                              <button
                                className="btn btn-small btn-secondary"
                                onClick={() => window.open(getImageUrl(file.url), '_blank')}
                              >
                                👁️ View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="files-summary">
                      <p><strong>Total Files:</strong> {userFiles.length}</p>
                      <p><strong>Total Size:</strong> {formatBytes(userFiles.reduce((sum, f) => sum + f.size, 0))}</p>
                    </div>
                  </div>
                )}
              </>
            )}

            <button className="btn btn-secondary" onClick={() => setSelectedUserForFiles(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Grant Tokens Modal */}
      {showGrantTokenModal && (
        <div className="modal-overlay" onClick={() => setShowGrantTokenModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Grant Tokens to {grantTokenUserName}</h2>
            <form onSubmit={handleGrantTokens}>
              <div className="form-group">
                <label className="form-label">Token Amount (1-10,000)</label>
                <input
                  type="number"
                  className="form-input"
                  value={grantTokenAmount}
                  onChange={(e) => setGrantTokenAmount(parseInt(e.target.value) || 0)}
                  min="1"
                  max="10000"
                  required
                  autoFocus
                />
                <p className="help-text">Maximum 10,000 tokens per week across all users</p>
              </div>
              
              <div className="form-group">
                <label className="form-label">Reason</label>
                <textarea
                  className="form-input"
                  value={grantTokenReason}
                  onChange={(e) => setGrantTokenReason(e.target.value)}
                  required
                  rows={3}
                  placeholder="e.g., Testing assistance, Customer support, Promotional grant"
                />
                <p className="help-text">This will be recorded in the transaction ledger</p>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowGrantTokenModal(false);
                    setGrantTokenAmount(100);
                    setGrantTokenReason('');
                  }}
                  disabled={grantTokenLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={grantTokenLoading}
                >
                  {grantTokenLoading ? 'Granting...' : 'Grant Tokens'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
